import { gql } from "lib/apolloClient";
import { questionFieldsFragments } from "Worksheet/modules/WorksheetFragments";

export const associatedEntityTypesFragments = {
  genericTagDetails: gql`
    fragment genericTagDetailsItem on GenericTag {
      id
      label
      tagType: type
    }
  `,
  subjectDetails: gql`
    fragment subjectDetailsItem on Subject {
      id
      name
    }
  `,
  gradeDetails: gql`
    fragment gradeDetailsItem on Grade {
      id
      name
    }
  `,
  plannerElementNode: gql`
    fragment plannerElementNodeItemV2 on PlannerElementNode {
      id
      label
      depth
      elementType: type
      associatedParents {
        id
        ... on Subject {
          id
          name
          isArchived
        }
      }
    }
  `,
  plannerElementNodeBasic: gql`
    fragment plannerElementNodeBasicItem on PlannerElementNode {
      id
      label
    }
  `,
};

export const questionBasicDetailsFragment = {
  basicDetails: gql`
    fragment questionBasicDetails on Question {
      id
      createdBy {
        id
        firstName
        lastName
        profileImage
      }
      questionScore
      questionType {
        id
        uid
        type
      }
    }
  `,
};

export const questionFragments = {
  libraryFeedDetails: gql`
    fragment libraryFeedDetailsItem on Question {
      ...questionBasicDetails
      libraryUid
      fields {
        ...questionFieldsItem
      }
      questionType {
        id
        uid
        config
      }
      answerFields {
        ...questionFieldsItem
      }
      conversation @include(if: $fetchConversations) {
        id
        unreadMessageCount
      }
      difficulty
      status
      isArchived
      createdBy {
        ... on Staff {
          id
          firstName
          lastName
          profileImage
        }
      }
      associatedEntities {
        ... on Grade {
          ...gradeDetailsItem
        }
        ... on Subject {
          ...subjectDetailsItem
        }
        ... on GenericTag {
          ...genericTagDetailsItem
        }
        ... on PlannerElementNode {
          ...plannerElementNodeItemV2
        }
        __typename
      }
    }
    ${questionBasicDetailsFragment.basicDetails}
    ${questionFieldsFragments.questionFields}
    ${associatedEntityTypesFragments.genericTagDetails}
    ${associatedEntityTypesFragments.subjectDetails}
    ${associatedEntityTypesFragments.gradeDetails}
    ${associatedEntityTypesFragments.plannerElementNode}
  `,
  questionLibraryFeedDetails: gql`
    fragment questionFeedDetailsItem on Question {
      ...questionBasicDetails
      libraryUid
      fields {
        ...questionFieldsItem
      }
      questionType {
        id
        uid
        config
      }
      answerFields {
        ...questionFieldsItem
      }
      conversation @include(if: $fetchConversations) {
        id
        unreadMessageCount
      }
      status
      isArchived
      createdBy {
        ... on Staff {
          id
          firstName
          lastName
          profileImage
        }
      }
    }
    ${questionBasicDetailsFragment.basicDetails}
    ${questionFieldsFragments.questionFields}
  `,
  subQuestionFeedDetails: gql`
    fragment subQuestionFeedDetailsItem on Question {
      ...questionBasicDetails
      fields {
        ...questionFieldsItem
      }
      questionType {
        id
        uid
        config
      }
      answerFields {
        ...questionFieldsItem
      }
    }
    ${questionBasicDetailsFragment.basicDetails}
    ${questionFieldsFragments.questionFields}
  `,
  /**
   * V2 fragment for the internal tool (QuestionBank) feed.
   */
  questionLibraryFeedDetailsV2: gql`
    fragment questionLibraryFeedDetailsItem on Question {
      id
      questionType {
        id
        uid
        config
      }
      questionScoreV2
      questionTitleV2 {
        id
        uid
        value
      }
      libraryUid
      status
      isArchived
      difficulty
      createdBy {
        ... on Staff {
          id
          firstName
          lastName
          profileImage
        }
      }
      conversation {
        id
        unreadMessageCount
      }
      children {
        edges {
          id
          sequence
        }
      }
      parentEntity {
        ... on OrganizationResource {
          id
        }
      }
    }
  `,
  questionDetails: gql`
    fragment questionDetailsItem on Question {
      id
      libraryUid
      questionType {
        id
        uid
        type
        config
      }
      parentEntity {
        ... on OrganizationResource {
          id
        }
      }
      isArchived
      questionScore
      difficulty
      status
      updatedAt
      createdBy {
        ... on Staff {
          id
          firstName
          lastName
          profileImage
        }
      }
      fields {
        ...questionFieldsItem
      }
      answerFields {
        ...questionFieldsItem
      }
      conversation {
        id
        type
        label
        parentType
        unreadMessageCount
        users {
          id
          firstName
        }
      }
      reviewedBy {
        ... on Staff {
          id
          firstName
          lastName
          profileImage
        }
      }
      associatedEntities {
        ... on Grade {
          ...gradeDetailsItem
        }
        ... on Subject {
          ...subjectDetailsItem
        }
        ... on GenericTag {
          ...genericTagDetailsItem
        }
        ... on PlannerElementNode {
          ...plannerElementNodeItemV2
        }
      }
    }
    ${questionFieldsFragments.questionFields}
    ${associatedEntityTypesFragments.genericTagDetails}
    ${associatedEntityTypesFragments.subjectDetails}
    ${associatedEntityTypesFragments.gradeDetails}
    ${associatedEntityTypesFragments.plannerElementNode}
  `,
};

export const organizationResourceQuestionFragment = gql`
  fragment organizationResourceQuestionItem on OrganizationResource {
    id
    resourceType
    item {
      ...questionDetailsItem
    }
    childrenV2(first: null, orderBy: DISPLAY_SEQUENCE, orderByDirection: ASC) {
      resourceTypeBasedCount {
        totalCount
      }
      edges {
        node {
          id
          item {
            ...subQuestionFeedDetailsItem
          }
        }
      }
    }
  }
  ${questionFragments.questionDetails}
  ${questionFragments.subQuestionFeedDetails}
`;

export const subQuestionFeedDetailsV2Fragment = gql`
  fragment subQuestionFeedDetailsV2Item on Question {
    id
    questionType {
      id
      uid
      config
    }
    parentEntity {
      ... on OrganizationResource {
        id
      }
    }
    questionScoreV2
    questionTitleV2 {
      id
      uid
      value
    }
    parentIdV2
    parentTypeV2
    sequence
    fields {
      ...questionFieldsItem
    }
    answerFields {
      ...questionFieldsItem
    }
  }
  ${questionFieldsFragments.questionFields}
`;

export const questionDetailsV2Fragment = gql`
  fragment questionDetailsV2Item on Question {
    id
    libraryUid
    questionType {
      id
      uid
      type
      config
    }
    parentEntity {
      ... on OrganizationResource {
        id
      }
    }
    parentIdV2
    parentTypeV2
    sequence
    isArchived
    questionScoreV2
    questionTitleV2 {
      id
      uid
      value
    }
    difficulty
    status
    updatedAt
    createdBy {
      ... on Staff {
        id
        firstName
        lastName
        profileImage
      }
    }
    fields {
      ...questionFieldsItem
    }
    answerFields {
      ...questionFieldsItem
    }
    conversation {
      id
      type
      label
      parentType
      unreadMessageCount
      users {
        id
        firstName
      }
    }
    reviewedBy {
      ... on Staff {
        id
        firstName
        lastName
        profileImage
      }
    }
    associatedEntities {
      ... on Grade {
        ...gradeDetailsItem
      }
      ... on Subject {
        ...subjectDetailsItem
      }
      ... on GenericTag {
        ...genericTagDetailsItem
      }
      ... on PlannerElementNode {
        ...plannerElementNodeItemV2
      }
    }
    children {
      edges {
        ...subQuestionFeedDetailsV2Item
      }
    }
  }
  ${questionFieldsFragments.questionFields}
  ${associatedEntityTypesFragments.genericTagDetails}
  ${associatedEntityTypesFragments.subjectDetails}
  ${associatedEntityTypesFragments.gradeDetails}
  ${associatedEntityTypesFragments.plannerElementNode}
  ${subQuestionFeedDetailsV2Fragment}
`;
